import { LocalStorageKeys } from "@/constants/local-storage-keys";
import { decodeJWT } from "@/utils/jwt";
import { Capacitor } from "@capacitor/core";
import { addBreadcrumb, captureMessage } from "@sentry/react";

/**
 * Telemetry for *involuntary* sign-outs — the intermittent "I got logged out
 * and I didn't do anything / it wasn't 7 days" reports.
 *
 * Records why a sign-out happened so the next occurrence is diagnosable in
 * Sentry. The operation version also invalidates token refreshes that were
 * started before explicit logout. The user id/email are already
 * attached to every event via setUser, so we never log tokens, claims, or ids —
 * only booleans, enums, and timestamps.
 *
 * The discriminators we capture:
 *  - cause `api_401_expired_jwt` + small `msSinceExpiry` (seconds/minutes) →
 *    token-refresh race / stale cached token (the intermittent bug).
 *  - cause `api_401_expired_jwt` + large `msSinceExpiry` (days) → ordinary
 *    Clerk session expiry (e.g. the 7-day window).
 *  - cause `api_401_missing_jwt` → no token was attached (Clerk not ready /
 *    getToken returned empty).
 *  - cause `clerk_session_lost` → Clerk flipped to signed-out with no 401 and
 *    no explicit logout (e.g. iOS WebView evicting Clerk's session storage).
 *    Tagged `sessionEnd`: `early` (before Clerk's end time — a bug) or
 *    `unknown` (no end time saved). Sign-outs at/after the end time are the
 *    normal 7-day limit and only leave a breadcrumb.
 */

type Api401Context = { url?: string; authorizationHeader?: unknown };

// PrivateAuthGuard's sign-out fires shortly after signOutOnce / explicit logout.
// Within this window we attribute it to the already-recorded cause instead of
// double-reporting it as a mysterious session loss.
const RECENT_CAUSE_WINDOW_MS = 30_000;
let lastSignal: { kind: "api_401" | "explicit"; at: number } | null = null;
let authOperationVersion = 0;

// In-flight refreshes must not restore credentials after the user logs out.
export const getAuthOperationVersion = () => authOperationVersion;

const safe = <T>(fn: () => T, fallback: T): T => {
  try {
    return fn();
  } catch {
    return fallback;
  }
};

const hasItem = (key: string): boolean => safe(() => Boolean(localStorage.getItem(key)), false);
const platform = (): string => safe(() => Capacitor.getPlatform(), "unknown");
const isOnline = (): boolean => safe(() => navigator.onLine, true);
const clerkSessionPresent = (): boolean =>
  safe(
    () => Boolean((window as unknown as { Clerk?: { session?: unknown } }).Clerk?.session),
    false,
  );

// Ambient signals attached to every logout event. Booleans / enums only.
const buildAuthContext = () => ({
  online: isOnline(),
  platform: platform(),
  clerkSession: clerkSessionPresent() ? "present" : "absent",
  hasUserKey: hasItem(LocalStorageKeys.USER_KEY),
  hasAccessToken: hasItem(LocalStorageKeys.ACCESS_TOKEN_KEY),
  hasIdHeader: hasItem(LocalStorageKeys.ID_HEADER_KEY),
});

// "Did the app think this user was signed in?" Explicit logout clears these, and
// a never-signed-in user has none — so this filters those non-anomalous cases out.
const hasLocalAuthArtifacts = () =>
  hasItem(LocalStorageKeys.USER_KEY) || hasItem(LocalStorageKeys.ACCESS_TOKEN_KEY);

const decodeBearer = (authorizationHeader: unknown) => {
  if (typeof authorizationHeader !== "string" || !authorizationHeader.startsWith("Bearer ")) {
    return { present: false as const };
  }
  const token = authorizationHeader.slice("Bearer ".length).trim();
  if (!token) return { present: false as const };
  const decoded = decodeJWT(token);
  return {
    present: true as const,
    exp: typeof decoded?.exp === "number" ? decoded.exp : undefined,
    iat: typeof decoded?.iat === "number" ? decoded.iat : undefined,
  };
};

const endpointPath = (url?: string): string | undefined => {
  if (!url) return undefined;
  return safe(() => new URL(url, "http://relative.invalid").pathname, url.split("?")[0]);
};

/** Mark a user-initiated logout so the follow-on guard sign-out isn't flagged. */
export const markExplicitLogout = () => {
  authOperationVersion++;
  lastSignal = { kind: "explicit", at: Date.now() };
};

/** Called from the 401 interceptor when it signs the user out. */
export const reportApi401SignOut = (context: Api401Context) => {
  lastSignal = { kind: "api_401", at: Date.now() };

  const jwt = decodeBearer(context.authorizationHeader);
  const ts = Date.now();
  const cause = jwt.present ? "api_401_expired_jwt" : "api_401_missing_jwt";
  const msSinceExpiry = jwt.present && jwt.exp !== undefined ? ts - jwt.exp * 1000 : undefined;
  const tokenAgeMs = jwt.present && jwt.iat !== undefined ? ts - jwt.iat * 1000 : undefined;
  const ctx = buildAuthContext();
  const data = { ...ctx, endpoint: endpointPath(context.url), msSinceExpiry, tokenAgeMs };

  addBreadcrumb({ category: "auth", level: "warning", message: `Sign-out: ${cause}`, data });
  captureMessage("Involuntary sign-out (API 401)", {
    level: "warning",
    tags: {
      feature: "auth",
      issue: "involuntary_signout",
      cause,
      online: String(ctx.online),
      platform: ctx.platform,
    },
    extra: {
      ...data,
      tokenPresent: jwt.present,
      hasExpClaim: jwt.present ? jwt.exp !== undefined : undefined,
    },
  });
};

// Clerk ends every session at a fixed time (7 days after sign-in on our plan).
// We remember that end time while signed in, so a later sign-out can be told
// apart: at/after the end time is expected; before it is a real bug.
type SessionInfo = { id: string | null; expireAt: number | null; reported: boolean };

// Tolerates a device clock that's slightly off.
const EXPIRY_CLOCK_SKEW_MS = 5 * 60_000;

const readSessionInfo = (): SessionInfo | null =>
  safe(() => {
    const raw = localStorage.getItem(LocalStorageKeys.CLERK_SESSION_INFO_KEY);
    return raw ? (JSON.parse(raw) as SessionInfo) : null;
  }, null);

const writeSessionInfo = (info: SessionInfo) =>
  safe(
    () => localStorage.setItem(LocalStorageKeys.CLERK_SESSION_INFO_KEY, JSON.stringify(info)),
    undefined,
  );

/** Called while signed in, so a later sign-out can be compared with the session's end time. */
export const rememberActiveSession = ({ id, expireAt }: { id: string; expireAt: Date }) => {
  const expireAtMs = expireAt.getTime();
  const saved = readSessionInfo();
  // Same session, same end time: keep the record (and its `reported` flag) as is.
  if (saved?.id === id && saved.expireAt === expireAtMs) return;
  writeSessionInfo({ id, expireAt: expireAtMs, reported: false });
};

/**
 * Called from PrivateAuthGuard when Clerk reports signed-out. Reports only the
 * genuinely mysterious case: a lost session with local auth artifacts still
 * present, no 401 / explicit logout to explain it, and the session's end time
 * not yet reached. Each lost session is reported at most once.
 */
export const reportInvoluntarySignOut = () => {
  const recent =
    lastSignal && Date.now() - lastSignal.at < RECENT_CAUSE_WINDOW_MS ? lastSignal : null;

  if (recent?.kind === "explicit") return; // user-initiated
  if (recent?.kind === "api_401") return; // already captured at the 401 source
  if (!hasLocalAuthArtifacts()) return; // normal not-signed-in state, not a lost session

  const saved = readSessionInfo();
  if (saved?.reported) return; // already handled (route change or app reopened while signed out)

  const msBeforeExpiry =
    typeof saved?.expireAt === "number" ? saved.expireAt - Date.now() : undefined;
  const sessionEnd =
    msBeforeExpiry === undefined
      ? "unknown"
      : msBeforeExpiry <= EXPIRY_CLOCK_SKEW_MS
        ? "expected"
        : "early";

  writeSessionInfo({
    id: saved?.id ?? null,
    expireAt: saved?.expireAt ?? null,
    reported: true,
  });

  const ctx = { ...buildAuthContext(), sessionEnd, msBeforeExpiry };
  addBreadcrumb({
    category: "auth",
    level: "warning",
    message: "Sign-out: clerk_session_lost",
    data: ctx,
  });
  if (sessionEnd === "expected") return; // the normal session limit, not a bug

  captureMessage("Involuntary sign-out (Clerk session lost without 401)", {
    level: "warning",
    tags: {
      feature: "auth",
      issue: "involuntary_signout",
      cause: "clerk_session_lost",
      sessionEnd,
      online: String(ctx.online),
      platform: ctx.platform,
    },
    extra: ctx,
  });
};

/** Precursor breadcrumb: token refresh failed, an expired/near-expiry token is being served. */
export const breadcrumbStaleTokenServed = (token: string) => {
  const decoded = decodeJWT(token);
  const ts = Date.now();
  addBreadcrumb({
    category: "auth",
    level: "warning",
    message: "Token refresh returned empty — serving cached token",
    data: {
      msSinceExpiry: typeof decoded?.exp === "number" ? ts - decoded.exp * 1000 : undefined,
      tokenAgeMs: typeof decoded?.iat === "number" ? ts - decoded.iat * 1000 : undefined,
      online: isOnline(),
      clerkSession: clerkSessionPresent() ? "present" : "absent",
    },
  });
};

/**
 * A 401 was recovered by force-refreshing the token and retrying, instead of
 * signing the user out. Lets us measure how often the cold-boot race is now
 * caught rather than turning into an involuntary sign-out.
 */
export const breadcrumbToken401Recovered = () => {
  addBreadcrumb({
    category: "auth",
    level: "info",
    message: "401 recovered via forced token refresh",
    data: {
      online: isOnline(),
      platform: platform(),
      clerkSession: clerkSessionPresent() ? "present" : "absent",
    },
  });
};

/** Precursor breadcrumb: no token available, the request will go out unauthenticated. */
export const breadcrumbNoTokenAvailable = () => {
  addBreadcrumb({
    category: "auth",
    level: "warning",
    message: "No token available (Clerk returned empty) — request will be unauthenticated",
    data: {
      online: isOnline(),
      clerkSession: clerkSessionPresent() ? "present" : "absent",
      hasAccessToken: hasItem(LocalStorageKeys.ACCESS_TOKEN_KEY),
    },
  });
};

/** Test-only: reset the cross-call cause marker. */
export const __resetLogoutTelemetry = () => {
  lastSignal = null;
  authOperationVersion = 0;
};
